import React, { Suspense, useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import { Canvas } from "@react-three/fiber";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Leva } from "leva";
import { Button } from "antd";
import { Vector2, Vector3, WebGLRenderTarget, PCFSoftShadowMap, Euler, UnsignedByteType, RGBAFormat, LinearFilter, Color } from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { GammaCorrectionShader } from "three/examples/jsm/shaders/GammaCorrectionShader.js";
import { SMAAPass } from "three/examples/jsm/postprocessing/SMAAPass.js";

import "../../../index.css";
import styles from "../../../styles/ModelViewer/ProjectModelView.module.css";
import { getHdr } from "../HDRConstant.js";
import playIcon from "../../../assets/icon/play_icon.svg";
import LoadingScreen from "./LoadingScreen.js";
import ErrorScreen from "./ErrorScreen.js";
import Scene from "./Scene.js";
import ARButton from "./ARButton.js";
import ARScene from "./ARScene.js";
import Model from "./Model.js";
import { fetchModelMaterialPresets } from "../../../services/api/materialPresetsApi.js";

// Hook personalizzato per il caricamento asincrono del modello 3D
export const useModelLoader = (url) => {
    const [error, setError] = useState(null);
    const [modelUrl, setModelUrl] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [fromCache, setFromCache] = useState(null);

    useEffect(() => {
        setIsLoaded(false);
        setError(null);
        setModelUrl(null);
        setLoadingProgress(0);
        setFromCache(null);

        async function loadModel(url) {
            if (!url || typeof url !== 'string') {
                console.error('URL del modello non valido:', url);
                setError('URL del modello non valido');
                setIsLoaded(true);
                return;
            }

            try {
                // Prima controlla la cache del browser
                let response;
                let useCache = typeof window !== 'undefined' && "caches" in window;

                if (useCache) {
                    const cache = await caches.open('models-cache');
                    response = await cache.match(url);

                    if (!response) {
                        // Se non in cache, scarica il modello
                        // console.log("🔵 Modello non in cache, scaricando:", url);
                        setFromCache(false);
                        response = await fetch(url);

                        // Clona la risposta prima di metterla in cache perché può essere letta una sola volta
                        const responseToCache = response.clone();
                        await cache.put(url, responseToCache);
                    } else {
                        // console.log("✅ Modello recuperato dalla cache:", url);
                        setFromCache(true);
                    }
                } else {
                    //Ambiente senza cache: scarica direttamente
                    setFromCache(false);
                    response = await fetch(url);
                }

                // Gestione del progresso di download
                const reader = response.body.getReader();
                const contentLength = +response.headers.get('Content-Length');
                let receivedLength = 0;
                const chunks = [];
                let done = false;
                while (!done) {
                    const { done: isDone, value } = await reader.read();
                    done = isDone;
                    if (!isDone) {
                        chunks.push(value);
                        receivedLength += value.length;
                        // Aggiorna la percentuale di progresso
                        const progress = (receivedLength / contentLength) * 100;
                        setLoadingProgress(Math.round(progress));
                    }
                }
                // Ricostruzione del blob e creazione URL locale per il modello
                const blob = new Blob(chunks);
                const urlBlob = URL.createObjectURL(blob);
                setModelUrl(urlBlob);
                setIsLoaded(true);
                setLoadingProgress(100);

            } catch (error) {
                console.error("Errore nel caricamento del modello:", error);
                setError(error.message);
                setIsLoaded(true);
            }
        }

        if (url) {
            loadModel(url);
        }

        return () => {
            if (modelUrl) {
                URL.revokeObjectURL(modelUrl);
            }
        };
    }, [url]);

    return { error, modelUrl, isLoaded, loadingProgress, setError, fromCache };
};

// Componente principale che gestisce la visualizzazione 3D e AR
const ThreeJSViewer = forwardRef(function ThreeJSViewer(props, ref) {
    // Recupero delle props del progetto dallo store Redux
    const viewerProps = useSelector((state) => {
        return state.brandViewer;
    });
    const project = viewerProps[0];
    // Estrazione delle props e degli stati globali/di routing
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const fovParam = +queryParams.get("fov");
    const mobileFov = +queryParams.get("fovMobile") || 50;

    // Stati locali per modalità, animazioni, AR, info modello, ecc.
    const [isValidatedMode, setIsValidateMode] = useState(false); // Modalità validazione attiva
    const [isUsingProjectSettings, setIsUsingProjectSettings] = useState(false); // Usa impostazioni progetto
    const [isMobileView, setIsMobileView] = useState(false); // Vista mobile/desktop
    const [hasAnimations, setHasAnimations] = useState(false); // Il modello ha animazioni
    const [isAnimatedOn, setIsAnimatedOn] = useState(false); // Animazione in corso
    const [isFirstAnimation, setIsFirstAnimation] = useState(false); // Prima animazione
    const [animationTitle, setAnimationTitle] = useState(""); // Titolo animazione
    const [animationDuration, setAnimationDuration] = useState(0); // Durata animazione
    const [scalingStarted, setScalingStarted] = useState(false); // Gesture di scala attiva in AR
    const [modelScale, setModelScale] = useState(1); // Scala attuale del modello in AR
    const [materialPresetsMap, setMaterialPresetsMap] = useState(new Map()); // Preset dei materiali del modelInView
    const [hasUserInteracted, setHasUserInteracted] = useState(props.isRenderer !== undefined || props.isMaterialPresets !== undefined); // Flag per indicare se l'utente ha interagito con il modello

    // Ref per accedere a oggetti Three.js (renderer, scena, camera)
    const glRef = useRef(null);
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const composerRef = useRef(null);
    const smaaRef = useRef(null);
    const ssaaRef = useRef(2.0); // try 2.0 for still quality; 1.5 if too heavy
    const [isRendering, setIsRendering] = useState(false);
    const [isInAR, setIsInAR] = useState(false); // Modalità AR attiva

    useImperativeHandle(ref, () => ({
        getScene: () => sceneRef.current,
        onRender: (filename, width, height, options = {}) => handleRenderClick(filename, width, height, options),
    }))

    // Stato per info geometriche del modello (dimensione e centro)
    const [modelInfo, setModelInfo] = useState({ sizes: { x: 0, y: 0, z: 0 }, center: { x: 0, y: 0, z: 0 } });

    // Selezione del modello corrente e modalità di modifica
    const selected = props.initialModel;
    const isEditModeOn = props.isEditModeOn;

    // Recupero del modello dal Redux store
    const modelInView = useSelector((state) =>
        state.modelGlasses.find((model) => model.id === selected)
    );

    // URL per la visualizzazione
    const urlView = props.urlView;

    // Recupero HDR e parametri di default
    const hdrNum = project?.hdr;
    const hdrPath = getHdr(hdrNum);

    const DEG_2_RAD = Math.PI / 180;
    
    const defaultValues = {
        backgroundColor: "#ffffff",
        backgroundImage: "",
        isBackgroundImage: false,
    };

    const comeBackTitle = "Come Back";

    // Determinazione dell'URL del modello da caricare
    const modelUrl = props.singleView ? props.singleSrc : modelInView?.urlGlobalComplete;
    // const modelUrl = 'https://firebasestorage.googleapis.com/v0/b/arshades-7e18a.appspot.com/o/glasses_data%2FMBoeOVaMPMe4qgLJIKfP%2Ftest_test_0HhrBnfie7sAUO88KHNN%2FTEST%202_test_test_complete.glb?alt=media&token=2c3d0ca2-d1ca-4681-9690-63c877241894';

    // Hook personalizzato per il caricamento del modello
    const { error, modelUrl: loadedModelUrl, isLoaded, loadingProgress, setError, fromCache } = useModelLoader(modelUrl);
    if (props.isRenderer && error) {
        console.error("Errore durante il caricamento del modello:", error.message);
    } else if (props.isRenderer && !error && loadedModelUrl) {
        console.log("Modello caricato con successo:", loadedModelUrl);
    }

    // URL per il modello in formato usdz (per AR su iOS)
    const modelUsdzUrl = props.singleUsdzSrc ? props.singleUsdzSrc : modelInView?.urlUsdzComplete;

    // Parametri per la scena 3D
    const SCENE_PARAMS = {
        src: isEditModeOn ? "" : project?.url ? project?.url : loadedModelUrl,
        alt: project?.alt,
        cameraControls: project?.cameraControls ?? true,
        autoRotate: project?.autoRotate ?? false,
        disableZoom: project?.disableZoom ?? false,
        lightExposition: project?.lightExposition ?? 1,
        shadowSmooth: project?.shadowSmooth ?? 0.3,
        shadowIntensity: project?.shadowIntensity ?? 0.5,
        hdr: (isValidatedMode && !isUsingProjectSettings) || hdrPath === 0 ? getHdr(8) : hdrPath,
        ar: "true",
        backgroundImage: isValidatedMode && !isUsingProjectSettings ? "" : project?.backgroundImage,
        skybox: project?.transparent,
        fieldOfView: fovParam ? fovParam : project?.fieldOfView ? +project?.fieldOfView.replace('deg', '') : 23,
        backgroundColor:
            isValidatedMode && !isUsingProjectSettings
                ? "#ffffff"
                : !project?.transparent
                    ? project?.backgroundColor ?? "#ffffff"
                    : "#ffffff",
        isBackgroundImage:
            isValidatedMode && !isUsingProjectSettings
                ? false
                : project?.isBackgroundImage === "true",
        title: project?.modelTitle ?? false,
        exposure: project?.lightExposition ?? "1.0",
        cameraOrbit: project?.cameraOrbit ?? "",
        useReflect: queryParams.get("reflect") === "true",
        useNoShadows: queryParams.get("noShadows") === "true",
        debugMode: queryParams.get("debug") === "true",
        modelMinY: modelInfo ? modelInfo.center.y - (modelInfo.sizes.y / 2) : 0,
    };

    // Preset dei materiali del modello
    const MODEL_PRESETS = (modelInView && materialPresetsMap.size > 0)
        ? materialPresetsMap
        : props.modelMaterialPresets;

    // Notifica il parent quando cambia fromCache
    useEffect(() => {
        if (props.onModelCacheStatus) {
            props.onModelCacheStatus(fromCache);
        }
    }, [fromCache]);

    // Effetto per gestire la visualizzazione mobile/desktop
    useEffect(() => {
        const checkMobile = () => {
            setIsMobileView(window.innerWidth <= 700);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, [project?.fieldOfView, isMobileView]);

    // Effetto per inizializzare l'effect composer per il post-processing dei render
    useEffect(() => {
        const gl = glRef.current;
        const scene = sceneRef.current;
        const camera = cameraRef.current;

        if (gl && scene && camera) {
            // Inizializzazione dell'effect composer
            // if (props.isRenderer) {
            //   console.log("Effect Composer initialization...");
            //   console.log("WebGL capabilities:", JSON.stringify(gl.capabilities));
            //   console.log("Pixel ratio:", gl.getPixelRatio());
            // }
        
            // 1) Capabilities
            const isWebGL2 = gl.capabilities.isWebGL2 === true;
            // console.log("isWebGL2", isWebGL2);
            const maxSamples = isWebGL2 ? (gl.capabilities.maxSamples ?? 0) : 0;
            // console.log("maxSamples", maxSamples);
        
            // 2) Supersampling (not mandatory but recommended in software)
            //    Set 1.0, 1.5 or 2.0 based on CPU/time budget.
            const SSAA = ssaaRef.current;
        
            gl.setPixelRatio(1);
            const base = gl.getSize(new Vector2());
            const w = Math.floor(base.x * SSAA);
            const h = Math.floor(base.y * SSAA);
        
            // 3) MSAA if possible
            const useMSAA = isWebGL2 && maxSamples > 0;
            const samples = useMSAA ? Math.min(4, maxSamples) : 0;
            
            // Render target
            const renderTarget = new WebGLRenderTarget(w, h, {
                samples,
                format: RGBAFormat,
                type: UnsignedByteType,
                minFilter: LinearFilter,
                magFilter: LinearFilter,
                stencilBuffer: false,
                depthBuffer: true
            });

            // Effect Composer
            const effectComposer = new EffectComposer(gl, renderTarget);
            effectComposer.setSize(w, h);

            // Render Pass
            const renderPass = new RenderPass(scene, camera);
            renderPass.clearAlpha = 0;
            effectComposer.addPass(renderPass);

            // Unreal Bloom Pass
            const unrealBloomPass = new UnrealBloomPass();
            unrealBloomPass.strength = 0.5;
            unrealBloomPass.radius = 0;
            unrealBloomPass.threshold = 0;
            unrealBloomPass.enabled = false;
            effectComposer.addPass(unrealBloomPass);

            // Questi due pass devono essere necessariamente gli ultimi
            // Gamma Correction Pass
            const gammaCorrectionPass = new ShaderPass(GammaCorrectionShader);
            gammaCorrectionPass.enabled = false;
            effectComposer.addPass(gammaCorrectionPass);

            // Antialias fallback
            if (samples === 0) {
                const smaa = new SMAAPass(w, h); // <- real dimensions!
                effectComposer.addPass(smaa);
                smaaRef.current = smaa;
            } else {
                smaaRef.current = null;
            }

            composerRef.current = effectComposer;
        }

        // Gestisci il resize anche per il composer
        // Handle resize: keep SSAA and re-align SMAA
        const onResize = () => {
            const newBase = gl.getSize(new Vector2());
            const newW = Math.floor(newBase.x * ssaaRef.current);
            const newH = Math.floor(newBase.y * ssaaRef.current);
            composerRef.current.setSize(newW, newH);
            
            if (smaaRef.current) {
                // Some versions of SMAAPass don't have setSize – recreate if needed:
                composerRef.current.removePass(smaaRef.current);
                const newSmaa = new SMAAPass(newW, newH);
                composerRef.current.addPass(newSmaa);
                smaaRef.current = newSmaa;
            }
        };

        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [glRef.current, sceneRef.current, cameraRef.current]);

    // Effetto per aggiornare la camera in base alle dimensioni/centro del modello
    useEffect(() => {
        const modelSize = new Vector3(modelInfo.sizes.x, modelInfo.sizes.y, modelInfo.sizes.z).length();
        if (modelSize > 0) {
            // Ricrea un Vector3 dal centro serializzato del modello
            const center = new Vector3(modelInfo.center.x, modelInfo.center.y, modelInfo.center.z);

            if (SCENE_PARAMS.cameraOrbit) {
                // Se sono specificati parametri di orbita, li usa per posizionare la camera
                if (!isMobileView) {
                    cameraRef.current.fov = SCENE_PARAMS.fieldOfView;
                }
                const sphericalParams = SCENE_PARAMS.cameraOrbit.split(' ').filter(param => param.trim() !== '');

                // Estrai i valori numerici dai parametri di orbita
                const getNumericValue = (str) => {
                    const match = str.match(/(\d+(?:\.\d+)?)/);
                    return match ? parseFloat(match[1]) : 0;
                };

                // Trova il parametro del raggio
                const radiusParam = sphericalParams.find(param => param.includes('m'));
                const radius = radiusParam ? getNumericValue(radiusParam) : 1.0;

                // Preleva e converte i parametri theta e phi in radianti
                const thetaParam = sphericalParams[0];
                const phiParam = sphericalParams[1];

                let theta = thetaParam.includes('deg') ? getNumericValue(thetaParam) * DEG_2_RAD : getNumericValue(thetaParam);
                let phi = phiParam.includes('deg') ? getNumericValue(phiParam) * DEG_2_RAD : getNumericValue(phiParam);

                // Posiziona la camera usando coordinate sferiche
                const cameraPosition = new Vector3();
                cameraPosition.setFromSphericalCoords(radius, phi, theta);
                cameraRef.current.position.copy(cameraPosition);
            } else {
                // Se non ci sono parametri di orbita, utilizza il FOV salvato in SCENE_PARAMS.fieldOfView o usa mobileFov
                if (!isMobileView) {
                    cameraRef.current.fov = SCENE_PARAMS.fieldOfView;
                } else {
                    cameraRef.current.fov = mobileFov;
                }

                // Posiziona la camera rispetto al centro del modello
                cameraRef.current.position.copy(center);
                cameraRef.current.position.y += modelSize * 0.6;
                cameraRef.current.position.z += modelSize * 2.3;
            }

            // Fai puntare la camera al centro del modello
            cameraRef.current.lookAt(center);

            // Aggiorna la matrice di proiezione della camera
            cameraRef.current.updateProjectionMatrix();

            // Imposta isSceneReady per segnalare che la scena è pronta
            window.isSceneReady = true;
        }
    }, [modelInfo]);

    // Effetto per caricare i preset dei materiali del modello, se in multiView
    useEffect(() => {
        if (!modelInView || !modelInView.list_material_presets) return;

        fetchModelMaterialPresets(modelInView).then((presetsMap) => {
            setMaterialPresetsMap(presetsMap);
        });

        return () => {
            setMaterialPresetsMap(new Map());
        }

    }, [modelInView]);

    useEffect(() => {
        if (!modelInView) return;
        if (hasUserInteracted) {
            setHasUserInteracted(false);
        }
    }, [modelInView]);

    // Effetto per attivare la modalità validazione in base ai parametri della query
    useEffect(() => {
        if (queryParams?.get("sceneSettings") === "neutral" && !shouldHideSwitchButton()) {
            setIsValidateMode(true);
        }
    }, [isValidatedMode]);

    // Effetto per gestire la chiusura della sessione AR
    useEffect(() => {
        const gl = glRef.current;
        if (!gl || !gl.xr) return;

        const session = gl.xr.getSession();
        if (!session) return;

        const onEnd = () => {
            console.log("WebXR session ended");
            setIsInAR(false);
        };

        session.addEventListener("end", onEnd);

        return () => {
            session.removeEventListener("end", onEnd);
            session.end().catch(console.warn);
        };
    }, [glRef.current]);

    // Funzione per determinare se nascondere il bottone di switch modalità
    const shouldHideSwitchButton = () => {
        return (
            project?.backgroundColor === defaultValues.backgroundColor &&
            project?.backgroundImage === defaultValues.backgroundImage &&
            project?.isBackgroundImage === defaultValues.isBackgroundImage &&
            hdrPath === 0
        );
    };

    // Gestione errori di caricamento modello
    const handleModelError = (errorMessage) => {
        console.error('Errore nel modello:', errorMessage);
        setError(errorMessage);
    };

    // Switch tra modalità validazione e impostazioni progetto
    const toggleSettings = () => {
        setIsUsingProjectSettings((prevState) => !prevState);
    };

    // Gestione delle animazioni disponibili nel modello
    const handleHasAnimations = (available, name, duration) => {
        if (available) {
            setHasAnimations(true);
            setIsFirstAnimation(true);
            // console.log('duration', duration);
            setAnimationDuration(duration);

            // Imposta il titolo dell'animazione in base al nome
            if (name === "explosion_glasses_an_1") {
                setAnimationTitle("EXPAND");
            } else if (name.startsWith("CO_")) {
                setAnimationTitle("APPLY CLIP-ONS");
            } else if (name.startsWith("IL_")) {
                setAnimationTitle("CHANGE LENSES");
            } else {
                setAnimationTitle("ANIMATE");
            }
        }
    };

    // Avvia l'animazione e gestisce il suo ciclo
    const startAnimation = async () => {
        setIsAnimatedOn(true);
        setTimeout(() => {
            setIsAnimatedOn(false);
            setIsFirstAnimation((alreadyAnimated) => (!alreadyAnimated));
            // console.log('TimeOut!')
        }, animationDuration * 1000);
    };

    // Funzioni per la gestione della modalità AR
    const handleARStart = () => {
        setIsInAR(true);
        setHasUserInteracted(false);
    };

    const handleAREnd = () => {
        setIsInAR(false);
        setHasUserInteracted(false);
    };

    // Funzione per uscire manualmente dalla modalità AR
    const handleExitARClick = () => {
        const gl = glRef.current;
        if (!gl || !gl.xr) return;

        const session = gl.xr.getSession();
        if (session) {
            session.end().catch(console.warn);
        }
    }

    // Funzione per effettuare un render manuale e scaricare uno screenshot
    const handleRenderClick = async (filename, width, height, options = {}) => {
        setIsRendering(true);
        const gl = glRef.current;
        const scene = sceneRef.current;
        const camera = cameraRef.current;
        const composer = composerRef.current;
        const modelSize = new Vector3(modelInfo.sizes.x, modelInfo.sizes.y, modelInfo.sizes.z).length();
        
        console.log('handleRenderClick', filename, width, height, JSON.stringify(options));

        const { download = false, cameraPosition, cameraRotation, cameraFov, cameraLookAt } = options;

        if (gl && scene && camera && composer) {
            // Salva dimensioni originali del renderer, da applicare poi al composer
            const originalSize = gl.getSize(new Vector2());
            const originalPixelRatio = gl.getPixelRatio();
            // Salva proprietà camera
            const originalCameraPosition = camera.position.clone();
            const originalCameraRotation = camera.rotation.clone();
            // console.log('originalCameraPosition', JSON.stringify(originalCameraPosition));
            // console.log('originalCameraRotation', JSON.stringify(originalCameraRotation));
            const originalCameraFov = camera.fov;

            // Imposta nuove dimensioni per il render ad alta qualità
            const hiW = Math.floor(width * ssaaRef.current);
            const hiH = Math.floor(height * ssaaRef.current);

            gl.setSize(hiW, hiH);
            gl.setPixelRatio(1);
            // Imposta nuove dimensioni anche al composer
            composer.setSize(hiW, hiH);
            composer.setPixelRatio(1);

            if (smaaRef.current) {
                composer.removePass(smaaRef.current);
                const newSmaa = new SMAAPass(hiW, hiH);
                composer.addPass(newSmaa);
                smaaRef.current = newSmaa;
            }

            // Aggiorna l'aspect ratio della camera in base alle nuove dimensioni
            camera.aspect = hiW / hiH;
            
            // Aggiorna la posizione e la rotazione della camera
            const center = new Vector3(modelInfo.center.x, modelInfo.center.y, modelInfo.center.z);
            if (cameraPosition !== undefined) {
                const cameraPositionVector = new Vector3(cameraPosition[0], cameraPosition[1], cameraPosition[2]);
                camera.position.set(center.x + cameraPositionVector.x, center.y + cameraPositionVector.y, center.z + cameraPositionVector.z);
            } else {
                camera.position.set(center.x + 0.392, center.y + 0.240, center.z + 0.642);
            }
            if (cameraRotation !== undefined) {
                const cameraRotationVector = new Euler(cameraRotation[0] * DEG_2_RAD, cameraRotation[1] * DEG_2_RAD, cameraRotation[2] * DEG_2_RAD);
                camera.rotation.set(cameraRotationVector.x, cameraRotationVector.y, cameraRotationVector.z);
            } else {
                camera.rotation.set(-15.39 * DEG_2_RAD, 31.20 * DEG_2_RAD, 6.40 * DEG_2_RAD);
            }

            // cambia la fov della camera
            if (cameraFov !== undefined) {
                camera.fov = cameraFov;
            // } else {
            //     // Aggiorna il FOV in base al modello 
            //     const fovCoeff = 4 * modelSize / Math.PI;
            //     camera.fov = 10 - (10 * fovCoeff);
            //     // camera.fov = 9.5; // con clip-on su pavimento
            }
            console.log('Render camera fov:', camera.fov);

            if (cameraLookAt !== undefined) {
                camera.lookAt(center.x + cameraLookAt[0], center.y + cameraLookAt[1], center.z + cameraLookAt[2]);
            } else {
                camera.lookAt(center.x - 0.006, center.y - 0.016, center.z);
                // camera.lookAt(center.x - 0.006, center.y - 0.0235, center.z); // con clip-on su pavimento
            }
            camera.updateProjectionMatrix();

            // se il parametro reflect è true, rende l'immagine dopo 10 ms per vedere il riflesso senza distorsione
            if (SCENE_PARAMS.useReflect) {
                // Usa Promise per gestire il timeout correttamente
                const finalDataURL = await new Promise((resolve) => {
                    setTimeout(() => {
                        const dataURL = renderImage(hiW, hiH, download, filename);
                        
                        restoreSceneSettings(originalSize, originalPixelRatio, originalCameraPosition, originalCameraRotation, originalCameraFov, center);

                        resolve(dataURL);
                    }, 10);
                });
                
                return { filename: filename, dataURL: finalDataURL };
            } else {
                const finalDataURL = renderImage(hiW, hiH, download, filename);
                restoreSceneSettings(originalSize, originalPixelRatio, originalCameraPosition, originalCameraRotation, originalCameraFov, center);
                
                return { filename: filename, dataURL: finalDataURL };
            }         
        }

        return null;
    }

    const restoreSceneSettings = (size, pixelRatio, cameraPosition, cameraRotation, cameraFov, center) => {
        const gl = glRef.current;
        const camera = cameraRef.current;
        const composer = composerRef.current;

        // Ripristina dimensioni originali del renderer e del composer
        gl.setSize(size.x, size.y);
        gl.setPixelRatio(pixelRatio);
        composer.setSize(size.x, size.y);
        composer.setPixelRatio(pixelRatio);

        // Ripristina lo stato originale della camera
        camera.aspect = size.x / size.y;
        camera.position.set(cameraPosition.x, cameraPosition.y, cameraPosition.z);
        camera.rotation.set(cameraRotation.x, cameraRotation.y, cameraRotation.z);
        camera.fov = cameraFov;
        camera.lookAt(center);
        camera.updateProjectionMatrix();
    }

    const renderImage = (width, height, download, filename) => {
        const gl = glRef.current;
        const scene = sceneRef.current;
        const composer = composerRef.current;
    
        console.log("Rendering scene with EffectComposer...");
        // Renderizza la scena con l'effect composer
        if (SCENE_PARAMS.useReflect) {
            // PRIMO RENDER: con background pieno per aggiornare il riflesso
            composer.render();
    
            // SECONDO RENDER: con background trasparente per esportazione PNG
            scene.background = null;
            composer.render();
        } else {
            composer.render();
        }
    
        // Estrae l'immagine dal canvas
        let finalDataURL;
        if (ssaaRef.current > 1) {
            const srcCanvas = gl.domElement; // hi-res
            const outCanvas = document.createElement("canvas");
            outCanvas.width = width;
            outCanvas.height = height;
            const ctx = outCanvas.getContext("2d");
            if (!ctx) return null;
    
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";
            ctx.drawImage(srcCanvas, 0, 0, srcCanvas.width, srcCanvas.height, 0, 0, width, height);
            finalDataURL = outCanvas.toDataURL("image/png");
        } else {
            // DPR=1 e canvas già a misura finale
            finalDataURL = gl.domElement.toDataURL("image/png");
        }
    
        // se download è true, scarica l'immagine
        if (download) {
            console.log("Saving file...");
            // Converti il dataURL in Blob
            function dataURLtoBlob(dataurl) {
                const arr = dataurl.split(",");
                const mime = arr[0].match(/:(.*?);/)[1];
                const bstr = atob(arr[1]);
                let n = bstr.length;
                const u8arr = new Uint8Array(n);
                while (n--) {
                    u8arr[n] = bstr.charCodeAt(n);
                }
                return new Blob([u8arr], { type: mime });
            }
    
            const blob = dataURLtoBlob(finalDataURL);
            const url = URL.createObjectURL(blob);
    
            const link = document.createElement("a");
            link.download = filename;
            link.href = url;
            link.click();
    
            // Libera la memoria dopo un po'
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        }
    
        if (SCENE_PARAMS.useReflect) {
            // Ripristina il background se è reflect
            scene.background = new Color(SCENE_PARAMS.backgroundColor);
        }

        setIsRendering(false);
        return finalDataURL;
    };

    // Render del componente
    return (
        <>
            {/* Schermata di caricamento */}
            <LoadingScreen
                progress={loadingProgress}
                isLoaded={isLoaded}
            />
            {/* Schermata di errore */}
            {error && <ErrorScreen message={error} />}

            {/* Bottone per cambiare modalità validazione/pubblica */}
            <div className={styles.viewAction}>
                {isValidatedMode && !shouldHideSwitchButton() && (
                    <Button onClick={toggleSettings} className={styles.validationButton}>
                        {isUsingProjectSettings ? "Validation Mode" : "Public Mode"}
                    </Button>
                )}
            </div>

            {/* Contenitore principale della scena */}
            <div style={{
                height: props.singleView ? "100%" : "83%",
                width: "100%",
                backgroundColor: SCENE_PARAMS.reflect ? "transparent" : SCENE_PARAMS.backgroundColor,
                backgroundPosition: isMobileView && !props.singleView ? 'bottom center' : '',
                backgroundSize: 'cover',
                backgroundImage: SCENE_PARAMS.isBackgroundImage
                    ? `url(${SCENE_PARAMS.backgroundImage})`
                    : "",
            }}>

                {/* Canvas 3D con React Three Fiber */}
                <Canvas
                    camera={{
                        position: [0, 0.125, 0.47],
                        fov: window.innerWidth <= 700 ? mobileFov : SCENE_PARAMS.fieldOfView,
                        near: 0.001,
                        far: 1000  // Aumentato per AR
                    }}
                    gl={{
                        antialias: true
                    }}
                    onCreated={({ gl, scene, camera }) => {
                        // Abilita XR e ombre
                        gl.xr.enabled = true;
                        gl.shadowMap.enabled = true;
                        gl.shadowMap.type = PCFSoftShadowMap;
                        glRef.current = gl;
                        sceneRef.current = scene;
                        cameraRef.current = camera;
                    }}
                    shadows={!SCENE_PARAMS.useNoShadows}
                    onPointerEnter={() => {
                        document.body.style.cursor = 'grab';
                    }}
                    onPointerLeave={() => {
                        document.body.style.cursor = 'default';
                    }}
                    onPointerDown={() => {
                        document.body.style.cursor = 'grabbing';
                        if (!hasUserInteracted) {
                            setHasUserInteracted(true);
                        }
                    }}
                    onPointerUp={() => {
                        document.body.style.cursor = 'grab';
                    }}
                >
                    <Suspense fallback={null}>
                        {/* Se in AR, mostra la scena AR, altrimenti la scena normale */}
                        {isInAR ? (
                            <ARScene
                                modelUrl={SCENE_PARAMS.src}
                                error={error}
                                scalingStarted={scalingStarted}         // Stato: gesture di scala attiva
                                setScalingStarted={setScalingStarted}   // Setter: aggiorna stato gesture
                                setModelScale={setModelScale}           // Setter: aggiorna scala modello
                            >
                                <Model
                                    url={SCENE_PARAMS.src}
                                    modelPresets={MODEL_PRESETS}
                                    onError={handleModelError}
                                    handleHasAnimations={handleHasAnimations}
                                    isFirstAnimation={isFirstAnimation}
                                    isAnimatedOn={isAnimatedOn}
                                    setModelInfo={setModelInfo}
                                    hasInteracted={true}
                                />
                            </ARScene>
                        ) : (
                            <Scene
                                error={error}
                                isMobileView={isMobileView}
                                mobileFov={mobileFov}
                                sceneParams={SCENE_PARAMS}
                                renderingStatus={isRendering}
                            >
                                <Model
                                    url={SCENE_PARAMS.src}
                                    modelPresets={MODEL_PRESETS}
                                    onError={handleModelError}
                                    handleHasAnimations={handleHasAnimations}
                                    isFirstAnimation={isFirstAnimation}
                                    isAnimatedOn={isAnimatedOn}
                                    inDebugMode={SCENE_PARAMS.debugMode}
                                    setModelInfo={setModelInfo}
                                    hasInteracted={hasUserInteracted}
                                />
                            </Scene>
                        )}
                    </Suspense>
                </Canvas>

                {/* Overlay per bottoni AR, animazione e info scala */}
                <div style={{ position: "relative" }}>
                    {/* Bottone per attivare la modalità AR */}
                    {glRef.current && !props.isRenderer && !props.isMaterialPresets && (
                        <ARButton
                            glRenderer={glRef.current}
                            urlView={urlView}
                            modelGlbUrl={modelUrl}
                            modelUsdzUrl={modelUsdzUrl}
                            onARStart={handleARStart}
                            onAREnd={handleAREnd}
                        />
                    )}

                    <div className="ar-overlay">
                        {/* Bottone per uscire dalla modalità AR */}
                        <button
                            onClick={handleExitARClick}
                            style={{
                                position: 'absolute',
                                justifyContent: 'center',
                                alignItems: 'center',
                                top: '20px',
                                right: '20px',
                                zIndex: 9999,
                                padding: '8px 12px',
                                backgroundColor: 'transparent',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '20px',
                                cursor: 'pointer',
                                userSelect: "none",
                                WebkitUserSelect: "none",
                                msUserSelect: "none",
                                touchAction: "manipulation",
                                WebkitTouchCallout: "none",
                                display: isInAR ? "flex" : "none"
                            }}>
                            X
                        </button>

                        {/* Visualizzazione della percentuale di scala durante la gesture di pinch */}
                        <h3 style={{
                            position: 'absolute',
                            justifyContent: 'center',
                            alignItems: 'center',
                            top: '20px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 9999,
                            padding: '8px 12px',
                            backgroundColor: 'transparent',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '20px',
                            cursor: 'pointer',
                            userSelect: "none",
                            WebkitUserSelect: "none",
                            msUserSelect: "none",
                            touchAction: "manipulation",
                            WebkitTouchCallout: "none",
                            display: scalingStarted ? "flex" : "none" // Mostra solo durante la gesture di scala
                        }}>
                            Scale: {`${Math.round(modelScale * 100)}%`}
                        </h3>

                        {/* Bottone per avviare l'animazione */}
                        {hasAnimations && (
                            <div className={styles.animationContainer}
                                style={{ display: isAnimatedOn ? "none" : "block" }}>
                                <Button
                                    onClick={startAnimation}
                                    className={styles.animation_button}
                                    icon={<img src={playIcon} className={styles.clipped_icon_button} alt="Play" />}
                                >
                                    {isFirstAnimation ? animationTitle : comeBackTitle}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Pannello di controllo Leva (per debug/parametri live) */}
                <Leva collapsed />
            </div>
        </>
    );
});

export default ThreeJSViewer;